import {
  CustomerAreaRoutes,
  createRoute,
  INFO_NOT_AVAILABLE_NA,
} from '@backoffice-frontend/common'
import { MoiaGrid, useTheme } from '@backoffice-frontend/patterns'
import { Restricted, useUserHasAccess } from '@backoffice-frontend/restricted'
import { BoumRight as Rights } from '@moia-dev/moia-token-claims'
import { ChildseatIcon, WheelchairIcon } from '@moia-dev/pace-icons'
import { Badge, Text } from '@moia-dev/pace-web'
import { Link } from 'react-router'
import {
  VEHICLE_MISSION_WAYPOINT_ACTION_HEIGHT,
  VEHICLE_MISSION_WAYPOINT_ACTION_MARGIN_HEIGHT,
} from '../../constants'
import type { VehicleMissionFccUpcomingWaypointAction } from '../../parseVehicle'
import type {
  VehicleMissionDeliveryActionFragment,
  VehicleMissionPickupActionFragment,
} from '../../vehicleMissionCard.hook'

const isTripAction = (
  action: VehicleMissionFccUpcomingWaypointAction,
): action is
  | VehicleMissionDeliveryActionFragment
  | VehicleMissionPickupActionFragment => {
  return (
    action.__typename === 'FccWaypointDeliveryAction' ||
    action.__typename === 'FccWaypointPickupAction'
  )
}

export const VehicleMissionWaypointAction = ({
  waypointAction,
}: {
  waypointAction: VehicleMissionFccUpcomingWaypointAction
}) => {
  const theme = useTheme()

  const canSeeCustomerData = useUserHasAccess({
    requiredRights: [Rights.BACKOFFICE_CUSTOMER_R],
  })

  const customer =
    canSeeCustomerData &&
    isTripAction(waypointAction) &&
    waypointAction.customer

  const isPickUpAction = waypointAction.__typename === 'FccWaypointPickupAction'

  const passengerAmountChangeIndicator = isPickUpAction ? '+' : '-'

  const seats = isTripAction(waypointAction) ? waypointAction.seats : undefined

  const wheelchairSeats = seats?.wheelchairs ?? 0
  const childSeats = (seats?.childSeats ?? 0) + (seats?.boosterSeats ?? 0)

  const isActionCancelled = isPickUpAction
    ? waypointAction.tripIsCancelled
    : false

  return (
    <MoiaGrid
      gridGap={0}
      gridTemplateColumns={`56px auto auto`}
      gridTemplateRows={`${VEHICLE_MISSION_WAYPOINT_ACTION_HEIGHT}px`}
      alignItems="center"
      justifyContent={'start'}
      css={{
        margin: `${VEHICLE_MISSION_WAYPOINT_ACTION_MARGIN_HEIGHT}px ${theme.space.Space4} 0  ${theme.space.Space4}`,
      }}
    >
      <div
        css={{
          gridColumnStart: 2,
          color: theme.semantic.ColorContentDefault,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          textDecoration: isActionCancelled ? 'line-through' : undefined,
        }}
      >
        <span
          css={theme => ({
            fontWeight: theme.typography.fontWeightBold,
            width: '2ch',
            display: 'inline-block',
            textDecoration: isActionCancelled ? 'line-through' : undefined,
          })}
        >
          <Text>{`${passengerAmountChangeIndicator}${seats?.seats}`}</Text>
        </span>{' '}
        {customer ? (
          <Link
            target="_blank"
            to={createRoute(CustomerAreaRoutes.Show.route, {
              customerId: customer.id,
            })}
            css={theme => ({
              fontWeight: theme.typography.fontWeightBold,
              color: theme.semantic.ColorActionSecondaryDefault,
              textDecoration: isActionCancelled ? 'line-through' : 'none',
            })}
          >
            <Restricted
              requiredRights={[Rights.BACKOFFICE_CUSTOMER_NAME_R]}
              replacement={customer.id}
            >
              <Text>
                {[customer.firstName, customer.lastName].join(' ').trim()}
              </Text>
            </Restricted>
          </Link>
        ) : (
          <Text>{INFO_NOT_AVAILABLE_NA}</Text>
        )}
      </div>
      <div css={{ display: 'flex', justifyContent: 'flex-end' }}>
        {wheelchairSeats > 0 && (
          <div
            css={{
              paddingLeft: theme.space.Space2,
              alignItems: 'center',
              display: 'flex',
              color: theme.semantic.ColorContentDefault,
              gap: theme.space.Space1,
              textDecoration: isActionCancelled ? 'line-through' : undefined,
            }}
          >
            <WheelchairIcon
              css={{
                height: 16,
                width: 16,
                fill: theme.semantic.ColorContentDefault,
              }}
            />{' '}
            {passengerAmountChangeIndicator}
            {wheelchairSeats}
          </div>
        )}
        {childSeats > 0 && (
          <div
            css={{
              paddingLeft: theme.space.Space2,
              alignItems: 'center',
              display: 'flex',
              color: theme.semantic.ColorContentDefault,
              gap: theme.space.Space1,
              textDecoration: isActionCancelled ? 'line-through' : undefined,
            }}
          >
            <ChildseatIcon css={{ height: 16, width: 16 }} />{' '}
            {passengerAmountChangeIndicator}
            {childSeats}
          </div>
        )}
        {isActionCancelled ? (
          <div
            css={{
              marginLeft: theme.space.Space1,
            }}
          >
            <Badge label={'Cancelled'} variant={'critical'} />
          </div>
        ) : null}
      </div>
    </MoiaGrid>
  )
}
